import React, { useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Users, GraduationCap, Heart } from "lucide-react";

export default function MegasusFoundation() {
  const [open, setOpen] = useState(false);

  const handleSubmit = (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    setOpen(false);
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-8">
            <h3 className="text-2xl font-bold mb-6 text-center">Volunteer Form</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input className="w-full border rounded-lg px-4 py-2" placeholder="Full Name" required />
              <input className="w-full border rounded-lg px-4 py-2" placeholder="Email" type="email" required />
              <input className="w-full border rounded-lg px-4 py-2" placeholder="Phone Number" required />
              <input className="w-full border rounded-lg px-4 py-2" placeholder="City" />
              <textarea className="w-full border rounded-lg px-4 py-2" rows={4} placeholder="Why do you want to volunteer?"></textarea>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg">Submit</button>
                <button type="button" onClick={() => setOpen(false)} className="flex-1 border py-2 rounded-lg">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="text-2xl font-bold text-blue-700">Megasus Foundation</div>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#about" className="hover:text-blue-600">About</a>
            <a href="#programs" className="hover:text-blue-600">Programs</a>
            <a href="#impact" className="hover:text-blue-600">Impact</a>
            <a href="#gallery" className="hover:text-blue-600">Gallery</a>
            <a href="#story" className="hover:text-blue-600">Story</a>
            <a href="#contact" className="hover:text-blue-600">Contact</a>
          </nav>

          <button className="bg-blue-600 text-white px-5 py-2 rounded-xl shadow hover:bg-blue-700">
            Donate
          </button>
        </div>
      </header>

      <section className="relative h-[600px] flex items-center">
        <img
          src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b"
          className="absolute inset-0 w-full h-full object-cover"
          alt="Students learning together"
        />

        <div className="absolute inset-0 bg-black/50"></div>

        <div className="relative max-w-7xl mx-auto px-6 text-white">
          <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6 max-w-3xl">
            Empowering Youth in Odisha Through Education
          </h1>

          <p className="text-lg max-w-xl opacity-90 mb-8">
            Megasus Foundation provides educational training, digital literacy and skill development programs that help communities build a brighter future.
          </p>

          <div className="flex gap-4">
            <button onClick={() => setOpen(true)} className="bg-blue-600 px-6 py-3 rounded-xl font-semibold shadow hover:bg-blue-700">
              Join as Volunteer
            </button>

            <button className="border border-white px-6 py-3 rounded-xl">
              Learn More
            </button>
          </div>
        </div>
      </section>

      <section id="about" className="py-24 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <img
            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644"
            className="rounded-3xl shadow-xl"
            alt="Education training program"
          />

          <div>
            <h2 className="text-3xl font-bold mb-6">About Megasus Foundation</h2>

            <p className="text-gray-600 leading-relaxed">
              Megasus Foundation is a non-profit organization working to improve access to education and employment opportunities in Odisha. Our mission is to empower students and youth with the skills needed for the modern world.
            </p>

            <p className="text-gray-600 leading-relaxed mt-4">
              Through training programs, digital education, and grassroots initiatives we aim to create a more educated and self-reliant society.
            </p>

            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="p-6 bg-blue-50 rounded-2xl">
                <BookOpen className="text-blue-600 mb-3" />
                <h4 className="font-semibold">Education</h4>
              </div>

              <div className="p-6 bg-green-50 rounded-2xl">
                <GraduationCap className="text-green-600 mb-3" />
                <h4 className="font-semibold">Training</h4>
              </div>

              <div className="p-6 bg-orange-50 rounded-2xl">
                <Users className="text-orange-600 mb-3" />
                <h4 className="font-semibold">Community</h4>
              </div>

              <div className="p-6 bg-purple-50 rounded-2xl">
                <Heart className="text-purple-600 mb-3" />
                <h4 className="font-semibold">Impact</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="programs" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16">Our Programs</h2>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div whileHover={{ y: -6 }} className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <img src="https://images.unsplash.com/photo-1588072432836-e10032774350" className="h-48 w-full object-cover" alt="Classroom education" />
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-3">Educational Training</h3>
                <p className="text-gray-600 text-sm">Computer education, exam preparation, and learning programs for students and youth.</p>
              </div>
            </motion.div>

            <motion.div whileHover={{ y: -6 }} className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3" className="h-48 w-full object-cover" alt="Digital skill training" />
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-3">Skill Development</h3>
                <p className="text-gray-600 text-sm">Digital skills, vocational training, and career-oriented programs for employability.</p>
              </div>
            </motion.div>

            <motion.div whileHover={{ y: -6 }} className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <img src="https://images.unsplash.com/photo-1497486751825-1233686d5d80" className="h-48 w-full object-cover" alt="Community learning session" />
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-3">Community Education</h3>
                <p className="text-gray-600 text-sm">Awareness programs, workshops, and grassroots education initiatives.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="impact" className="py-24 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-16">Our Impact</h2>

          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <div className="text-5xl font-bold text-blue-600">1000+</div>
              <p className="text-gray-600 mt-2">Students Trained</p>
            </div>

            <div>
              <div className="text-5xl font-bold text-green-600">50+</div>
              <p className="text-gray-600 mt-2">Training Workshops</p>
            </div>

            <div>
              <div className="text-5xl font-bold text-orange-500">20+</div>
              <p className="text-gray-600 mt-2">Communities Reached</p>
            </div>
          </div>
        </div>
      </section>

      <section id="gallery" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16">Our Work</h2>

          <div className="grid md:grid-cols-3 gap-6">
            <img src="https://images.unsplash.com/photo-1529390079861-591de354faf5" className="rounded-2xl shadow" alt="Students studying" />
            <img src="https://images.unsplash.com/photo-1544717305-2782549b5136" className="rounded-2xl shadow" alt="Children learning" />
            <img src="https://images.unsplash.com/photo-1607746882042-944635dfe10e" className="rounded-2xl shadow" alt="Education support program" />
          </div>
        </div>
      </section>

      <section id="story" className="py-24 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <img src="https://images.unsplash.com/photo-1593113630400-ea4288922497" alt="Community education initiative" className="rounded-3xl shadow-xl object-cover w-full h-[380px]" />

          <div>
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>

            <p className="text-gray-600 mb-6 leading-relaxed">
              Megasus Foundation was created with a simple mission to make education, skills, and opportunities accessible to young people in Odisha. Through community programs, training workshops and digital education initiatives we aim to empower the next generation to build a brighter future.
            </p>

            <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/dQw4w9WgXcQ" title="Megasus Foundation Story" allowFullScreen></iframe>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-blue-700 text-white text-center">
        <h2 className="text-3xl font-bold mb-6">Be Part of the Change</h2>
        <p className="max-w-2xl mx-auto opacity-90 mb-8">Support our mission to bring education and opportunities to youth across Odisha.</p>

        <div className="flex justify-center gap-4">
          <button className="bg-white text-blue-700 px-6 py-3 rounded-xl font-semibold">Donate</button>
          <button onClick={() => setOpen(true)} className="border border-white px-6 py-3 rounded-xl">Volunteer</button>
        </div>
      </section>

      <footer id="contact" className="bg-gray-900 text-gray-300 py-10 text-center">
        <div className="text-lg font-semibold text-white mb-2">Megasus Foundation</div>
        <p>Odisha, India</p>
        <p className="text-sm mt-2">info@megasusfoundation.org</p>
      </footer>
    </div>
  );
}
